import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import swal from "sweetalert";
import "./Login.css";
import logo from "../assets/images/logo.png";
import {
  CheckAuth,
  LoginAPI,
  issueToken,
} from "../../redux/services/BasicAPIFunctions";
import { setAuth } from "../../redux/auth/slice";
import { clearStoredAuth, restoreAuth, snapshotAuth } from "../../lib/auth";

const EyeIcon = (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <path
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"
    />
    <circle
      cx="12"
      cy="12"
      r="3"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    />
  </svg>
);

const EyeOffIcon = (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <path
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 3l18 18M10.6 10.6a3 3 0 0 0 4.2 4.2M9.9 5.2A9.6 9.6 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3.1 3.9M6.1 6.1A17 17 0 0 0 2 12s3.5 7 10 7a9.6 9.6 0 0 0 2.1-.2"
    />
  </svg>
);

export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Enter a valid email address")
        .required("Email is required"),
      // Login only enforces presence — password policy belongs to the backend,
      // so a future policy change can't lock out valid accounts at the client.
      password: Yup.string().required("Password is required"),
    }),
    onSubmit: (values) => handleSubmit(values),
  });

  const handleSubmit = async (values) => {
    setLoading(true);

    // Preserve any current session, then hand off with a clean slate. On any
    // failure we roll back to `prevAuth`, so a mistyped password can never log
    // the user out or leave half-written tokens behind.
    const prevAuth = snapshotAuth();
    clearStoredAuth();

    const credentials = {
      email_id: values.email,
      password: values.password,
    };

    try {
      // 1. Gate token — authorizes the login request itself.
      const gate = await CheckAuth();
      const gateToken = gate?.data?.access_token;
      if (!gateToken) {
        throw new Error("Couldn't start a login session. Please try again.");
      }
      localStorage.setItem("authcheck", gateToken);

      // 2. Verify credentials.
      const login = await LoginAPI(credentials);
      if (!login?.data?.status) {
        throw new Error(login?.data?.message || "Invalid email or password.");
      }
      const { uuid, permission, profile } = login.data.response;

      // 3. Exchange the login uuid for the real access/refresh tokens.
      const issued = await issueToken({ uuid });
      const accessToken = issued?.data?.response?.access_token;
      const refresh = issued?.data?.response?.refresh_token;
      if (!accessToken) {
        throw new Error("Couldn't establish a session. Please try again.");
      }

      // 4. Commit — only now is the previous session replaced.
      dispatch(
        setAuth({
          token: accessToken,
          refreshToken: refresh,
          profile,
          permissions: permission,
        }),
      );
      navigate("/dashboard");
    } catch (error) {
      clearStoredAuth();
      restoreAuth(prevAuth);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong. Please try again.";
      swal({ text: message, icon: "error", button: "Close" });
    } finally {
      setLoading(false);
    }
  };

  const emailError = formik.touched.email && formik.errors.email;
  const passwordError = formik.touched.password && formik.errors.password;

  return (
    <div className="login-page">
      <aside className="login-brand" aria-hidden="true">
        <div className="login-brand__inner">
          <span className="login-brand__wordmark">NYSMAH</span>
          <p className="login-brand__tagline">Admin Dashboard</p>
        </div>
      </aside>

      <main className="login-main">
        <div className="login-card">
          <img className="login-card__logo" src={logo} alt="Nysmah" />
          <h1 className="login-card__title">Sign in</h1>
          <p className="login-card__subtitle">Access your admin dashboard</p>

          <form className="login-form" onSubmit={formik.handleSubmit} noValidate>
            <div className="login-field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                placeholder="you@nysmah.com"
                className={`login-input ${emailError ? "login-input--error" : ""}`}
                aria-invalid={Boolean(emailError)}
                aria-describedby={emailError ? "email-error" : undefined}
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {emailError ? (
                <div className="error" id="email-error">
                  {formik.errors.email}
                </div>
              ) : null}
            </div>

            <div className="login-field">
              <label htmlFor="password">Password</label>
              <div className="login-input-wrap">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  className={`login-input login-input--password ${
                    passwordError ? "login-input--error" : ""
                  }`}
                  aria-invalid={Boolean(passwordError)}
                  aria-describedby={passwordError ? "password-error" : undefined}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <button
                  type="button"
                  className="login-reveal"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                >
                  {showPassword ? EyeOffIcon : EyeIcon}
                </button>
              </div>
              {passwordError ? (
                <div className="error" id="password-error">
                  {formik.errors.password}
                </div>
              ) : null}
            </div>

            <button
              type="submit"
              className="login-submit"
              disabled={loading}
              aria-busy={loading}
            >
              {loading ? (
                <span className="login-spinner" aria-hidden="true" />
              ) : (
                "Sign in"
              )}
            </button>
          </form>
        </div>

        <p className="login-footnote">Nysmah — authorized admin access only</p>
      </main>
    </div>
  );
}
